import React from 'react';
import { Link } from 'react-router-dom';
import {
  PawPrint, Calendar, Syringe, Stethoscope, Users,
  ChevronRight, CheckCircle2, AlertTriangle, PlusCircle, ClipboardPlus,
} from 'lucide-react';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis,
  ResponsiveContainer, Tooltip,
} from 'recharts';
import { getOwners } from '../services/ownerService';
import { getPets } from '../services/petService';
import { getAppointments } from '../services/appointmentService';
import { getVaccinations } from '../services/vaccinationService';
import { getExaminations } from '../services/examinationService';
import { SkeletonDashboard } from '../components/Skeleton/Skeleton';
import { todayLocal, monthStartLocal, addDaysLocal } from '../utils/dateUtils';
import { getVaccinationStatus, getDaysLeft, getDueDate } from '../utils/vaccinationStatus';

const DAYS_TR = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];
const formatTime = (t?: string) => t || '--:--';
const formatDate = (d: string) =>
  new Date(d).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' });

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  if (status === 'completed') return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700">Tamamlandı</span>
  );
  if (status === 'cancelled') return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-rose-50 text-rose-700">İptal</span>
  );
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700">Bekliyor</span>
  );
};

const Dashboard: React.FC = () => {
  const [owners, setOwners] = React.useState<any[]>([]);
  const [pets, setPets] = React.useState<any[]>([]);
  const [appointments, setAppointments] = React.useState<any[]>([]);
  const [vaccinations, setVaccinations] = React.useState<any[]>([]);
  const [examinations, setExaminations] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    Promise.all([getOwners(), getPets(), getAppointments(), getVaccinations(), getExaminations()])
      .then(([o, p, a, v, e]) => {
        setOwners(o); setPets(p); setAppointments(a); setVaccinations(v); setExaminations(e);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const now = new Date();
  const today = todayLocal();

  // Bugün
  const todayAppts = appointments.filter((a: any) => a.date === today)
    .sort((a: any, b: any) => (a.time || '').localeCompare(b.time || ''));
  const todayExams = examinations.filter((e: any) => e.date === today);
  const todayVaccs = vaccinations.filter((v: any) => v.date === today);

  // Randevu durumları (donut için)
  const apptCompleted = todayAppts.filter((a: any) => a.status === 'completed').length;
  const apptPending = todayAppts.filter((a: any) => a.status !== 'completed' && a.status !== 'cancelled').length;
  const apptCancelled = todayAppts.filter((a: any) => a.status === 'cancelled').length;
  const donutData = todayAppts.length > 0
    ? [
        { name: 'Tamamlandı', value: apptCompleted, color: '#557236' },
        { name: 'Bekliyor', value: apptPending, color: '#F59E0B' },
        { name: 'İptal', value: apptCancelled, color: '#F87171' },
      ].filter(d => d.value > 0)
    : [{ name: 'Boş', value: 1, color: '#E8E2D4' }];

  // Bu ay ilerleme (aylık randevu hedefi: 30)
  const monthStart = monthStartLocal(now);
  const monthAppts = appointments.filter((a: any) => a.date >= monthStart && a.date <= today).length;
  const monthGoal = 30;
  const progressPct = Math.min(100, Math.round((monthAppts / monthGoal) * 100));

  // Haftalık hasta grafiği (son 6 hafta, her hafta yeni kayıt)
  const weeklyPatients = Array.from({ length: 6 }, (_, i) => {
    const we = addDaysLocal(-i * 7, now);
    const ws = addDaysLocal(-i * 7 - 6, now);
    return {
      label: `H${6 - i}`,
      Hasta: examinations.filter((e: any) => e.date >= ws && e.date <= we).length,
    };
  }).reverse();

  // Geciken aşılar (sonraki doz tarihi geçmiş ve sonradan yapılmamış)
  const overdueVaccs = vaccinations.filter((v: any) => getVaccinationStatus(v, vaccinations) === 'overdue');

  // Yaklaşan aşılar (bugün dahil 7 gün)
  const upcomingVaccs = vaccinations.filter((v: any) => {
    if (getVaccinationStatus(v, vaccinations) !== 'approaching') return false;
    const left = getDaysLeft(v);
    return left >= 0 && left <= 7;
  }).sort((a: any, b: any) => getDueDate(a).localeCompare(getDueDate(b)) || getDaysLeft(a) - getDaysLeft(b));

  // Son aktiviteler
  const recentItems = [
    ...examinations.map((e: any) => ({ ...e, _type: 'exam', _date: e.date })),
    ...vaccinations.map((v: any) => ({ ...v, _type: 'vacc', _date: v.date })),
    ...appointments.map((a: any) => ({ ...a, _type: 'appt', _date: a.date })),
  ].filter(x => x._date).sort((a, b) => new Date(b._date).getTime() - new Date(a._date).getTime()).slice(0, 5);

  // Sonraki randevular (bugün dahil, gelecek)
  const upcomingAppts = appointments
    .filter((a: any) => a.date >= today && a.status !== 'cancelled')
    .sort((a: any, b: any) => {
      if (a.date !== b.date) return a.date.localeCompare(b.date);
      return (a.time || '').localeCompare(b.time || '');
    })
    .slice(0, 8);

  if (loading) return <SkeletonDashboard />;

  const greetHour = now.getHours();
  const greet = greetHour < 12 ? 'Günaydın' : greetHour < 18 ? 'İyi günler' : 'İyi akşamlar';
  const dateStr = now.toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="space-y-5 py-5 px-4 lg:px-6">

      {/* ── Karşılama başlığı ── */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] mb-1" style={{ color: '#557236' }}>{dateStr}</p>
          <h1 className="text-[22px] lg:text-[26px] font-bold leading-tight" style={{ color: 'var(--color-text-strong)' }}>
            {greet}! 👋
          </h1>
          <p className="text-[13px] mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
            Kliniğinizin bugünkü durumuna bakın.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link to="/app/pets"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[12px] font-semibold transition-colors"
            style={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)', color: 'var(--color-text-strong)' }}>
            <PlusCircle className="w-3.5 h-3.5" /> Hasta Ekle
          </Link>
          <Link to="/app/examinations"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[12px] font-semibold text-white transition-colors"
            style={{ backgroundColor: '#557236' }}>
            <ClipboardPlus className="w-3.5 h-3.5" /> Yeni Muayene
          </Link>
        </div>
      </div>

      {/* ── 3 Üst Kart ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* Kart 1: Aylık İlerleme */}
        <div className="rounded-2xl p-5" style={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)' }}>
          <div className="flex items-center justify-between mb-1">
            <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>Aylık İlerleme</p>
            <span className="text-[11px] font-bold" style={{ color: '#557236' }}>{progressPct}%</span>
          </div>
          <p className="text-[11px] mb-3" style={{ color: '#557236' }}>
            +{monthAppts} randevu bu ay · Hedef: {monthGoal}
          </p>
          <div className="text-[44px] font-bold leading-none mb-4" style={{ color: 'var(--color-text-strong)' }}>
            {progressPct}<span className="text-[24px]">%</span>
          </div>
          {/* Progress bar */}
          <div className="relative h-2 rounded-full mb-3 overflow-hidden" style={{ backgroundColor: 'var(--color-bg-soft, #F0EBE1)' }}>
            <div
              className="absolute inset-y-0 left-0 rounded-full transition-all duration-700"
              style={{ width: `${progressPct}%`, background: 'linear-gradient(90deg, #557236, #8FA862)' }}
            />
          </div>
          <div className="flex justify-between text-[10px]" style={{ color: 'var(--color-text-subtle)' }}>
            <span>0</span>
            <span>{monthGoal} hedef</span>
          </div>
          <div className="mt-4 pt-3 flex justify-between" style={{ borderTop: '1px solid var(--color-border-soft)' }}>
            <div className="text-center">
              <p className="text-[18px] font-bold" style={{ color: 'var(--color-text-strong)' }}>{todayAppts.length + todayExams.length + todayVaccs.length}</p>
              <p className="text-[10px]" style={{ color: 'var(--color-text-muted)' }}>Bugün</p>
            </div>
            <div className="text-center">
              <p className="text-[18px] font-bold" style={{ color: 'var(--color-text-strong)' }}>{pets.length}</p>
              <p className="text-[10px]" style={{ color: 'var(--color-text-muted)' }}>Toplam Hasta</p>
            </div>
            <div className="text-center">
              <p className="text-[18px] font-bold" style={{ color: overdueVaccs.length > 0 ? '#C97B5E' : '#2B2B2B' }}>{overdueVaccs.length}</p>
              <p className="text-[10px]" style={{ color: 'var(--color-text-muted)' }}>Geciken Aşı</p>
            </div>
          </div>
        </div>

        {/* Kart 2: Bugünkü Randevular — Donut */}
        <div className="rounded-2xl p-5" style={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)' }}>
          <div className="flex items-center justify-between mb-1">
            <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>Bugünkü Randevular</p>
            <Link to="/app/appointments" className="text-[11px] font-medium" style={{ color: '#557236' }}>Tümü →</Link>
          </div>
          <p className="text-[11px] mb-3" style={{ color: 'var(--color-text-muted)' }}>
            {new Date().toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
          <div className="flex items-center justify-center relative" style={{ height: 130 }}>
            <ResponsiveContainer width="100%" height={130}>
              <PieChart>
                <Pie
                  data={donutData}
                  cx="50%"
                  cy="50%"
                  innerRadius={42}
                  outerRadius={58}
                  strokeWidth={0}
                  dataKey="value"
                >
                  {donutData.map((entry, index) => (
                    <Cell key={index} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <p className="text-[28px] font-bold leading-none" style={{ color: 'var(--color-text-strong)' }}>{todayAppts.length}</p>
              <p className="text-[10px]" style={{ color: 'var(--color-text-muted)' }}>Toplam</p>
            </div>
          </div>
          <div className="space-y-1.5 mt-2">
            {[
              { label: 'Tamamlandı', value: apptCompleted, pct: todayAppts.length > 0 ? Math.round(apptCompleted/todayAppts.length*100) : 0, color: '#557236' },
              { label: 'Bekliyor', value: apptPending, pct: todayAppts.length > 0 ? Math.round(apptPending/todayAppts.length*100) : 0, color: '#F59E0B' },
              { label: 'İptal', value: apptCancelled, pct: todayAppts.length > 0 ? Math.round(apptCancelled/todayAppts.length*100) : 0, color: '#F87171' },
            ].map(row => (
              <div key={row.label} className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: row.color }} />
                  <span className="text-[12px]" style={{ color: 'var(--color-text-strong)' }}>{row.label}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[12px] font-semibold" style={{ color: 'var(--color-text-strong)' }}>{row.value}</span>
                  <span className="text-[11px]" style={{ color: 'var(--color-text-subtle)' }}>({row.pct}%)</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Kart 3: Toplam Hastalar — Bar Chart */}
        <div className="rounded-2xl p-5" style={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)' }}>
          <div className="flex items-center justify-between mb-1">
            <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>Toplam Hastalar</p>
            <span className="text-[20px] font-bold" style={{ color: 'var(--color-text-strong)' }}>{pets.length.toLocaleString()}</span>
          </div>
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <span className="flex items-center gap-1 text-[11px]" style={{ color: 'var(--color-text-muted)' }}>
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#557236' }} />
              Aktif {pets.length}
            </span>
            <span className="flex items-center gap-1 text-[11px]" style={{ color: 'var(--color-text-muted)' }}>
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#E8E2D4' }} />
              Sahip {owners.length}
            </span>
          </div>
          <ResponsiveContainer width="100%" height={140}>
            <BarChart data={weeklyPatients} barSize={10} barGap={3}>
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#B0A99A' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: '#B0A99A' }} axisLine={false} tickLine={false} allowDecimals={false} width={20} />
              <Tooltip
                contentStyle={{ borderRadius: 8, border: '1px solid var(--color-border)', fontSize: 11 }}
                cursor={{ fill: '#F7F4ED' }}
              />
              <Bar dataKey="Hasta" fill="#557236" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <div className="pt-3 mt-1 flex justify-between" style={{ borderTop: '1px solid var(--color-border-soft)' }}>
            <div>
              <p className="text-[18px] font-bold" style={{ color: 'var(--color-text-strong)' }}>{owners.length}</p>
              <p className="text-[10px]" style={{ color: 'var(--color-text-muted)' }}>Hasta Sahibi</p>
            </div>
            <div>
              <p className="text-[18px] font-bold" style={{ color: 'var(--color-text-strong)' }}>{todayExams.length}</p>
              <p className="text-[10px]" style={{ color: 'var(--color-text-muted)' }}>Bugün Muayene</p>
            </div>
            <div>
              <p className="text-[18px] font-bold" style={{ color: 'var(--color-text-strong)' }}>{todayVaccs.length}</p>
              <p className="text-[10px]" style={{ color: 'var(--color-text-muted)' }}>Bugün Aşı</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Alt Bölüm: Randevu Tablosu + Sağ Panel ── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">

        {/* Sol: Yaklaşan Randevular tablosu */}
        <div className="lg:col-span-3 rounded-2xl overflow-hidden" style={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)' }}>
          <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid var(--color-border-soft)' }}>
            <div>
              <h2 className="text-[14px] font-bold" style={{ color: 'var(--color-text-strong)' }}>Yaklaşan Randevular</h2>
              <p className="text-[11px] mt-0.5" style={{ color: 'var(--color-text-muted)' }}>{upcomingAppts.length} randevu planlandı</p>
            </div>
            <Link to="/app/appointments"
              className="text-[11px] font-semibold px-3 py-1.5 rounded-lg transition-colors"
              style={{ backgroundColor: 'var(--color-bg-page)', color: '#557236', border: '1px solid var(--color-border)' }}>
              Tümünü Gör
            </Link>
          </div>

          {upcomingAppts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <CheckCircle2 className="w-10 h-10 mb-2" style={{ color: '#E8E2D4' }} />
              <p className="text-[13px]" style={{ color: 'var(--color-text-subtle)' }}>Yaklaşan randevu yok</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              {/* Table header */}
              <div className="grid px-5 py-2.5" style={{
                gridTemplateColumns: '2fr 1.5fr 1fr 1fr',
                borderBottom: '1px solid var(--color-border-soft)',
              }}>
                {['Hasta', 'Sahip', 'Tarih & Saat', 'Durum'].map(h => (
                  <span key={h} className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--color-text-subtle)' }}>{h}</span>
                ))}
              </div>
              {upcomingAppts.map((a: any, i: number) => (
                <div
                  key={a.id}
                  className="grid px-5 py-3 transition-colors"
                  style={{
                    gridTemplateColumns: '2fr 1.5fr 1fr 1fr',
                    borderBottom: i < upcomingAppts.length - 1 ? '1px solid #F7F4ED' : 'none',
                  }}
                  onMouseEnter={e => (e.currentTarget as HTMLElement).style.backgroundColor = '#FAFAF8'}
                  onMouseLeave={e => (e.currentTarget as HTMLElement).style.backgroundColor = ''}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: '#E8EFD9' }}>
                      <PawPrint className="w-3.5 h-3.5" style={{ color: '#3F5728' }} />
                    </div>
                    <span className="text-[12px] font-semibold truncate" style={{ color: 'var(--color-text-strong)' }}>
                      {a.pet?.name || a.petName || '-'}
                    </span>
                  </div>
                  <span className="text-[12px] truncate self-center" style={{ color: 'var(--color-text-muted)' }}>
                    {a.owner?.name || a.ownerName || '-'}
                  </span>
                  <div className="self-center">
                    <p className="text-[11px] font-semibold" style={{ color: 'var(--color-text-strong)' }}>
                      {a.date === today ? 'Bugün' : formatDate(a.date)}
                    </p>
                    <p className="text-[10px]" style={{ color: 'var(--color-text-subtle)' }}>{formatTime(a.time)}</p>
                  </div>
                  <div className="self-center">
                    <StatusBadge status={a.status || 'pending'} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Sağ Panel */}
        <div className="lg:col-span-2 flex flex-col gap-4">

          {/* Bu Hafta Aşılar */}
          <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)', flex: 1 }}>
            <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid var(--color-border-soft)' }}>
              <div>
                <h2 className="text-[13px] font-bold" style={{ color: 'var(--color-text-strong)' }}>Bu Hafta Aşılar</h2>
                <p className="text-[11px]" style={{ color: 'var(--color-text-muted)' }}>{upcomingVaccs.length} yaklaşan · {overdueVaccs.length > 0 ? <span style={{ color: '#C97B5E' }}>{overdueVaccs.length} gecikmiş</span> : 'Gecikme yok'}</p>
              </div>
              <Link to="/app/vaccinations" className="text-[11px] font-medium" style={{ color: '#557236' }}>Tümü →</Link>
            </div>
            {upcomingVaccs.length === 0 && overdueVaccs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <CheckCircle2 className="w-8 h-8 mb-1.5" style={{ color: '#E8E2D4' }} />
                <p className="text-[12px]" style={{ color: 'var(--color-text-subtle)' }}>Bu hafta aşı yok</p>
              </div>
            ) : (
              <div className="divide-y" style={{ '--tw-divide-opacity': 1 } as any}>
                {[...overdueVaccs.slice(0, 2).map(v => ({ ...v, _overdue: true })), ...upcomingVaccs.slice(0, 3)].slice(0, 4).map((v: any) => {
                  const diff = getDaysLeft(v);
                  const isOverdue = v._overdue || diff < 0;
                  return (
                    <div key={v.id} className="flex items-center gap-3 px-5 py-3">
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                        style={{ backgroundColor: isOverdue ? '#FAECE7' : '#FEF9EC' }}>
                        <Syringe className="w-3.5 h-3.5" style={{ color: isOverdue ? '#C97B5E' : '#D97706' }} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[12px] font-semibold truncate" style={{ color: 'var(--color-text-strong)' }}>
                          {v.vaccine_name || v.name || 'Aşı'}
                        </p>
                        <p className="text-[10px] truncate" style={{ color: 'var(--color-text-muted)' }}>
                          {v.pet?.name || ''}{v.pet?.owner?.name ? ` · ${v.pet.owner.name}` : ''}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-[11px] font-bold" style={{ color: isOverdue ? '#C97B5E' : '#D97706' }}>
                          {diff === 0 ? 'Bugün' : isOverdue ? `${Math.abs(diff)}g önce` : `${diff}g`}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Son Aktiviteler */}
          <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)', flex: 1 }}>
            <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid var(--color-border-soft)' }}>
              <h2 className="text-[13px] font-bold" style={{ color: 'var(--color-text-strong)' }}>Son Aktiviteler</h2>
              <span className="text-[11px]" style={{ color: 'var(--color-text-muted)' }}>Son 5 kayıt</span>
            </div>
            {recentItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <p className="text-[12px]" style={{ color: 'var(--color-text-subtle)' }}>Henüz kayıt yok</p>
              </div>
            ) : (
              <div className="divide-y" style={{ '--tw-divide-opacity': 1 } as any}>
                {recentItems.map((item: any, i: number) => {
                  const isExam = item._type === 'exam';
                  const isVacc = item._type === 'vacc';
                  const Icon = isExam ? Stethoscope : isVacc ? Syringe : Calendar;
                  const iconBg = isExam ? '#EEF2FF' : isVacc ? '#E8EFD9' : '#F0F4FF';
                  const iconColor = isExam ? '#6366F1' : isVacc ? '#3F5728' : '#557236';
                  const label = isExam ? 'Muayene' : isVacc ? 'Aşı' : 'Randevu';
                  const name = item.pet?.name || item.petName || '-';
                  return (
                    <div key={`${item._type}-${item.id}-${i}`} className="flex items-center gap-3 px-5 py-3 transition-colors"
                      onMouseEnter={e => (e.currentTarget as HTMLElement).style.backgroundColor = '#FAFAF8'}
                      onMouseLeave={e => (e.currentTarget as HTMLElement).style.backgroundColor = ''}>
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                        style={{ backgroundColor: iconBg }}>
                        <Icon className="w-3.5 h-3.5" style={{ color: iconColor }} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[12px] font-medium truncate" style={{ color: 'var(--color-text-strong)' }}>
                          <span style={{ color: 'var(--color-text-subtle)' }}>{label} · </span>{name}
                        </p>
                        <p className="text-[10px]" style={{ color: 'var(--color-text-subtle)' }}>{formatDate(item._date)}</p>
                      </div>
                      <ChevronRight className="w-3.5 h-3.5 shrink-0" style={{ color: '#E8E2D4' }} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
